/**
 * SEMILLA de Capacidades (canon F6, capacidades.html): el catálogo de las
 * 6 piezas enchufables del asesor es del SISTEMA, no de una API — no hay
 * endpoint que lo sirva, y por eso nunca está «vacío» (contrato). Cada
 * pieza lleva su etiqueta de máquina (IA / determinista / puerta del sí).
 * motor por pieza: memoria (ADR-6) y enrutado de skills ya son reales y
 * viven en el dominio; conocimiento curado espera su motor de catalogación
 * (F5b/M4, como en conocimiento.seed.tsx); los conectores BC/PBI son F5c
 * (integraciones — ver roadmap.md); vigilancia está diseñada pero sin fase
 * asignada todavía. Cuando cada motor real exista, esta semilla se sirve
 * desde su endpoint; el texto y los tags no cambian, porque son el mismo
 * contrato del prototipo.
 */
import type { ReactNode } from "react";

export type EstadoCapacidad = "activa" | "beta" | "prevista";

export interface Capacidad {
  slug: string;
  nombre: string;
  estado: EstadoCapacidad;
  prosa: ReactNode;
  tags: ReadonlyArray<{ kind: "ia" | "det" | "gate"; label: string }>;
  /** Sub-línea (mono, atenuada): prefijo literal + enlace interno opcional. */
  subPrefijo: string;
  subLink?: { to: string; label: string } | undefined;
}

export const CAPACIDADES: readonly Capacidad[] = [
  {
    slug: "memoria",
    nombre: "Memoria por casa",
    estado: "activa",
    prosa: "Criterios con puerta del sí, decisiones apuntadas y seguimientos que nunca se cierran solos. Lo que la casa confirma, el asesor lo recuerda — y lo demás, no.",
    tags: [{ kind: "gate", label: "puerta del sí" }, { kind: "det", label: "registro determinista" }],
    subPrefijo: "en las 23 casas · ADR-6",
  },
  {
    slug: "conocimiento",
    nombre: "Conocimiento curado",
    estado: "activa",
    prosa: "La biblioteca de 86 items con fuente citable, por capas y niveles. Sin ella el asesor responde igual — avisando de que improvisa.",
    tags: [{ kind: "det", label: "curación humana" }],
    subPrefijo: "86 items",
    subLink: { to: "/conocimiento", label: "la biblioteca" },
  },
  {
    slug: "enrutado",
    nombre: "Enrutado de skills",
    estado: "activa",
    prosa: "Cómo se elige el método de la casa que razona cada mensaje: la regla propone candidatos (palabras del tema por rareza, frases disparadoras, capa) y el modelo elige entre ellos, con el método anterior como opción. La misma que ejecuta el probador y que pinta la traza de auditoría.",
    tags: [{ kind: "det", label: "determinista" }],
    subPrefijo: "14 skills",
    subLink: { to: "/skills", label: "el catálogo" },
  },
  {
    slug: "bc",
    nombre: "Conector Business Central",
    estado: "activa",
    prosa: "Ventas, compras y tesorería de la casa, sincronizadas cada madrugada. Con él, el asesor pasa de criterio a cálculo — y cada traza lleva la hora del dato.",
    tags: [{ kind: "det", label: "determinista" }, { kind: "gate", label: "activación con registro" }],
    subPrefijo: "en 15 casas · última sync 07:42",
  },
  {
    slug: "pbi",
    nombre: "Conector Power BI",
    estado: "beta",
    prosa: "Los cuadros de mando de la casa como fuente citable. En beta con 4 casas; la activación de Grupo Vielsa espera su registro final en la cola.",
    tags: [{ kind: "det", label: "determinista" }, { kind: "gate", label: "activación con registro" }],
    subPrefijo: "en 4 casas",
    subLink: { to: "/cola", label: "1 activación en cola" },
  },
  {
    slug: "vigilancia",
    nombre: "Vigilancia",
    estado: "prevista",
    prosa: "Encargos del executive («avísame si el margen baja del 21 %») vigilados en background. Toda alerta llevará su porqué — o no se envía.",
    tags: [{ kind: "ia", label: "IA" }, { kind: "det", label: "umbrales deterministas" }],
    subPrefijo: "diseñada en el Despacho · pendiente de fase",
  },
] as const;
